import type { AuthorizedResourceAccessMap } from '@/permissions/adapter';
import {
  PERMISSION_ACCESS_KEYS,
  PERMISSION_ACTION_CODES,
  PERMISSION_RESOURCE_CODES,
} from '@/permissions/definitions';
import { hasActionAccess, hasResourceAccess } from '@/permissions/helpers';

const buildAdminAccessMap = (
  authorizedResourceAccess: AuthorizedResourceAccessMap = {},
): AuthorizedResourceAccessMap => {
  const adminAccessMap: AuthorizedResourceAccessMap = {
    ...authorizedResourceAccess,
  };

  Object.values(PERMISSION_RESOURCE_CODES).forEach((resourceCode) => {
    adminAccessMap[resourceCode] = true;
  });

  Object.entries(PERMISSION_ACTION_CODES).forEach(
    ([resourceKey, resourceActions]) => {
      const resourceCode =
        PERMISSION_RESOURCE_CODES[
          resourceKey as keyof typeof PERMISSION_RESOURCE_CODES
        ];

      Object.values(resourceActions).forEach((actionCode) => {
        adminAccessMap[`${resourceCode}:${actionCode}`] = true;
      });
    },
  );

  return adminAccessMap;
};

/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState:
    | {
        authorizedResourceAccess?: AuthorizedResourceAccessMap;
        currentTenantIsAdmin?: boolean;
      }
    | undefined,
) {
  const { authorizedResourceAccess, currentTenantIsAdmin } = initialState ?? {};
  const resourceAccess = currentTenantIsAdmin
    ? buildAdminAccessMap(authorizedResourceAccess)
    : authorizedResourceAccess;

  return {
    ...resourceAccess,
    [PERMISSION_ACCESS_KEYS.createContract]:
      hasActionAccess(
        resourceAccess,
        PERMISSION_RESOURCE_CODES.contractsMine,
        PERMISSION_ACTION_CODES.contractsMine.create,
      ) ||
      hasActionAccess(
        resourceAccess,
        PERMISSION_RESOURCE_CODES.contractsAll,
        PERMISSION_ACTION_CODES.contractsAll.create,
      ),
    [PERMISSION_ACCESS_KEYS.permissionManagement]:
      Boolean(currentTenantIsAdmin) &&
      hasResourceAccess(
        resourceAccess,
        PERMISSION_RESOURCE_CODES.permissionManagement,
      ),
    tenantAdmin: Boolean(currentTenantIsAdmin),
  };
}
